/* eslint-disable @reatom/unit-naming-rule */
import { atom, onConnect } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomPairResource } from '@root/entities/pair/atoms'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { createRecentCache } from '@root/shared/lib/recent-cache'

import { tradingPairAddressAtom } from './reatom-trading-pair-address'
import {
  tradingAuditVisibilityAtom,
  tradingPairAuditReaction,
} from './reatom-trading-pair-audit'
import { checkPairMigrationAction } from './reatom-trading-pair-migration'

import type { Unsubscribe } from '@reatom/framework'
import type { PairAddress } from '@root/entities/pair'

export const reatomTradingPair = ({
  network,
  pairAddress,
}: {
  network: Network.Network
  pairAddress: PairAddress
}) => {
  const pairStateAtom = reatomPairResource({ pairAddress, network })

  onConnect(pairStateAtom, (ctx) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)
    mainWebsocket.pairInfoSubscribeAction(ctx, {
      pairAddress,
      network: Network.toSupportedNetwork(network),
    })

    return () => {
      mainWebsocket.pairInfoUnsubscribeAction(ctx, {
        pairAddress,
        network: Network.toSupportedNetwork(network),
      })
    }
  })

  onConnect(pairStateAtom, (ctx) => {
    let unsubscribeAudit: Unsubscribe | undefined
    // Be careful with reaction here, because it will circularly depend on pairStateAtom
    const unsub = pairStateAtom.onChange((ctx, state) => {
      if (!AsyncStateN.isSuccess(state)) {
        return
      }
      const pair = state.value
      const redirected = checkPairMigrationAction(ctx, pair)
      if (redirected) {
        return
      }
      unsubscribeAudit = tradingPairAuditReaction(ctx, pair).unsubscribe
    })

    return () => {
      pairStateAtom.setNotInitiated(ctx)
      unsub()
      unsubscribeAudit?.()
      tradingAuditVisibilityAtom(ctx, { open: false, source: 'auto' })
    }
  })

  return pairStateAtom
}

/**
 * One resource per pair, not one per recompute.
 *
 * This atom spies two atoms that settle several times while the application
 * boots - the url's pair arrives through the router, the page's own effect
 * sets it again, the network resolves from the url or from preferences - and
 * it used to build a new `reatomPairResource` on each of them. A new resource
 * has no state, so it loads; the resource it replaced had the pair in its
 * state and that state is dropped. The registry could not cover the gap
 * either, because it holds models weakly and nothing else was holding this
 * one.
 *
 * Measured on staging, loading a pair page from cold: three `pair-info`
 * requests for the pair in the url, at 1,391ms, 3,215ms and 4,405ms, each one
 * a resource that had just been built and found nothing to reuse.
 *
 * Keyed by what the resource is actually about, so the address settling twice
 * on the same value costs nothing. Eight is for the pairs behind this one in
 * the history; beyond that the registry's weak references are the right
 * behaviour again.
 */
const tradingPairs = createRecentCache<ReturnType<typeof reatomTradingPair>>({
  size: 8,
})

const tradingPairModelAtom = atom((ctx) => {
  const pairAddress = ctx.spy(tradingPairAddressAtom)
  const network = ctx.spy(selectedNetworkAtom)

  return tradingPairs.getOrCreate(
    `${network.toString()}|${pairAddress.toString()}`,
    () => reatomTradingPair({ network, pairAddress }),
  )
})

export const tradingPairStateAtom = atom((ctx) => {
  const model = ctx.spy(tradingPairModelAtom)
  return ctx.spy(model)
})
export const tradingPairAtom = atom((ctx) => {
  const state = ctx.spy(tradingPairStateAtom)
  return state.value
})
export const tradingPairQuoteAtom = atom((ctx) => {
  const pair = ctx.spy(tradingPairAtom)
  return pair?.quote
}, 'tradingPairQuoteAtom')

export const tradingPairBaseAtom = atom((ctx) => {
  const pair = ctx.spy(tradingPairAtom)
  return pair?.base
}, 'tradingPairBaseAtom')
